import { UtilityType, utilityTypes } from "@/api/facilities";
import ImpactPill from "@/components/Impact/ImpactPill";
import Message from "@/components/Message/Message";
import StatusTags from "@/components/StatusTags/StatusTags";
import YearSelector, { YearStatus } from "@/components/YearSelector/YearSelector";
import {
  useGetFacility,
  useGetFacilityContracts,
  useGetFacilityYears,
  useUpdateFacilityUsed,
} from "@/hooks/useFacilities";
import useTranslation from "@/hooks/useTranslation";
import { IImpact } from "@/pages/dashboard/utils/types";
import { DisconnectOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Col, Row, Space, Tooltip } from "antd";
import axios from "axios";
import _ from "lodash";
import styles from "../FacilityDetail.module.scss";
import UtilityContractGroup from "./UtilityContractGroup";

export default function Utilities({
  facilityId,
  year,
  company_name,
}: {
  facilityId: number;
  year: number;
  company_name?: string;
}) {
  const contractGroups: UtilityType[] = [
    "electricity",
    "heat",
    "water",
    "cooling",
    "consumable",
    "residual_waste",
    "goods_and_services",
    "packaging",
  ];

  const { data, isLoading } = useGetFacilityContracts(facilityId);
  const { data: facilityData } = useGetFacility(facilityId, !isNaN(facilityId));
  const { data: yearData, isLoading: yearDataIsLoading } = useGetFacilityYears({ facilityId });
  const translations = useTranslation();

  const updateUsed = useUpdateFacilityUsed();

  const toggleUsed = (used: boolean) => {
    updateUsed.mutateAsync({
      facilityId: facilityId,
      setUsed: {
        year,
        used: used,
      },
    });
  };

  const facilityImpact = useQuery({
    queryKey: ["facilities", "detail", facilityId, "impact", year],
    queryFn: () =>
      axios.get<{ impact: IImpact }>("/facilities/" + facilityId + `/impact`, {
        params: { start_date: `${year}-01-01`, end_date: `${year}-12-31` },
      }),
  });

  const countByStatus = (status: string) =>
    contractGroups.flatMap((type) => data?.contracts[year][type].status).filter((x) => x === status)
      .length;

  const completeCount = countByStatus("complete");
  const incompleteCount = countByStatus("incomplete");

  const countByUsed = (used: boolean) =>
    contractGroups.flatMap((type) => data?.contracts[year][type].used).filter((x) => x === used)
      .length;

  const contractCountYear = _.sum(
    contractGroups.map((type) => data?.contracts[year][type].contracts.length)
  );

  const allowAllUnused =
    countByUsed(false) < utilityTypes.length &&
    contractCountYear === 0 &&
    !!facilityData?.address_is_valid;
  const allowAllUsed =
    countByUsed(false) === utilityTypes.length &&
    contractCountYear === 0 &&
    !!facilityData?.address_is_valid;

  return (
    <div>
      <Row
        gutter={[0, 8]}
        className={styles.header}
      >
        <Col span={24}>
          <span className={styles.title}>{translations.facility.detail.utilities.title}</span>
        </Col>
        <Col span={24}>
          <span>{translations.facility.detail.utilities.description}</span>
        </Col>
      </Row>
      <Row
        align={"middle"}
        className={styles.statusBar}
      >
        <Col flex={"auto"}>
          <Space size={12}>
            <YearSelector
              optionData={yearData?.map((yd): YearStatus => {
                const isComplete = contractGroups
                  .map((type) => data?.contracts[yd.year][type].status)
                  .flat()
                  .every((x) => x === "complete");

                return {
                  year: yd.year,
                  status: isComplete ? "complete" : "incomplete",
                  extraIcon: !yd.order_related ? (
                    <Tooltip title="Not related to purchase or sales orders">
                      <>
                        <DisconnectOutlined />
                      </>
                    </Tooltip>
                  ) : undefined,
                };
              })}
              isLoading={isLoading || yearDataIsLoading}
            />
            {allowAllUnused && (
              <Button
                size={"small"}
                onClick={() => toggleUsed(false)}
              >
                Switch all off
              </Button>
            )}
            {allowAllUsed && (
              <Button
                size={"small"}
                onClick={() => toggleUsed(true)}
              >
                Switch all on
              </Button>
            )}
          </Space>
        </Col>
        <Row
          align={"middle"}
          gutter={[18, 18]}
        >
          <Col>
            <ImpactPill
              impact={facilityImpact.data?.data.impact}
              loading={facilityImpact.isLoading || facilityImpact.isFetching}
              type="borderless"
            />
          </Col>
          <Col>
            {!!facilityData?.address_is_valid && (
              <StatusTags
                data={{
                  complete_count: completeCount,
                  incomplete_count: incompleteCount,
                }}
              />
            )}
          </Col>
        </Row>
      </Row>
      <div className={styles.listContainer}>
        {!isLoading && !facilityData?.address_is_valid ? (
          <Message type="note">
            In order to upload utility contracts, you need to enter a valid address.
          </Message>
        ) : (
          <>
            {year && (
              <div className={styles.list}>
                {contractGroups.map((type) => {
                  const contractGroup = year && data ? data.contracts[year][type] : undefined;

                  return (
                    <UtilityContractGroup
                      facilityId={facilityId}
                      key={type}
                      type={type}
                      year={year}
                      contractGroup={contractGroup}
                      isLoading={isLoading}
                      company_name={company_name}
                    />
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
